import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getMe, loginWithEmail, registerWithEmail } from '@/api/generated/authentication';
import type { LoginRequest, MeResponse, RegisterRequest } from '@/api/generated/model';
import { adminSignup, type AdminSignupRequest } from '@/api/adminAuth';
import { apiClient } from '@/lib/axios';
import { useAuthStore } from '@/store/authStore';

export { type LoginRequest, type MeResponse, type RegisterRequest };

export const authKeys = {
  me: ['auth', 'me'] as const,
};

export const useMe = () => {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated());
  return useQuery({
    queryKey: authKeys.me,
    queryFn: getMe,
    enabled: isAuthenticated,
    // Was Infinity — that left the app "signed in" for the whole 7-day
    // JWT lifetime with no re-verification, so a JWT invalidated
    // server-side (user row deleted, is_admin flipped off, or a full
    // sign-out that happened in another tab / device) wouldn't
    // surface until the user hit a route that fired a fresh API call.
    //
    // Now:
    //   • 30 s stale window — react-query treats data as fresh briefly
    //     so navigating between admin pages doesn't re-hit /auth/me.
    //   • Re-fetch on window focus — the classic "user came back from
    //     Gmail, session's dead, kick them out" case.
    //   • 60 s heartbeat — catches "user just left the tab open" — the
    //     axios interceptor already logs out + redirects on any 401 or
    //     404 from /auth/me, so a dead session lands them on /login
    //     within a minute of the invalidation.
    staleTime: 30_000,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
};

export const useLogin = () => {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const { token } = await loginWithEmail(credentials);
      // getMe() relies on the Zustand store for its token, but the store
      // hasn't been updated yet at this point. Pass the token explicitly.
      const { data: user } = await apiClient.get<MeResponse>('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { token, user };
    },
    onSuccess: ({ token, user }) => {
      setAuth(token, {
        id: user.id,
        email: user.email,
        whoopConnected: user.whoopConnected,
        isAdmin: user.isAdmin,
      });
      void queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
};

export const useRegister = () => {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (credentials: RegisterRequest) => {
      const { token } = await registerWithEmail(credentials);
      const { data: user } = await apiClient.get<MeResponse>('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { token, user };
    },
    onSuccess: ({ token, user }) => {
      setAuth(token, {
        id: user.id,
        email: user.email,
        whoopConnected: user.whoopConnected,
        isAdmin: user.isAdmin,
      });
      void queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
};

/**
 * Admin-app signup (email + password). Distinct from `useRegister`,
 * which drives the Flutter athlete flow at `/auth/register` and demands
 * the whole onboarding profile — this one only needs a credential and
 * grants the new account video-editor access.
 *
 * Mirrors `useLogin`'s shape: create, then read `/auth/me` with the
 * fresh token (the Zustand store isn't populated yet at that point, so
 * the header has to be passed explicitly) and seed the store from it.
 */
export const useAdminSignup = () => {
  const setAuth = useAuthStore((s) => s.setAuth);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: AdminSignupRequest) => {
      const { token } = await adminSignup(body);
      const { data: user } = await apiClient.get<MeResponse>('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { token, user };
    },
    onSuccess: ({ token, user }) => {
      setAuth(token, {
        id: user.id,
        email: user.email,
        whoopConnected: user.whoopConnected,
        isAdmin: user.isAdmin,
      });
      void queryClient.invalidateQueries({ queryKey: authKeys.me });
    },
  });
};

export const useLogout = () => {
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const queryClient = useQueryClient();

  return () => {
    clearAuth();
    queryClient.clear();
    window.location.replace('/login');
  };
};

/**
 * Cross-tab logout sync + external-signout detection.
 *
 * Mounted once at the app root. Two things it catches that useMe's
 * heartbeat can't:
 *
 *   1. Sign-out in another tab of THIS app. Zustand persist writes
 *      to localStorage, so the "clear auth" mutation fires a storage
 *      event in every other open tab. Without this listener the
 *      other tabs stay on their current page holding a stale token
 *      in memory until the 60 s heartbeat fires.
 *
 *   2. User signed out of Google in an unrelated tab (gmail.com,
 *      google.com), then came back to this tab. useMe's focus
 *      refetch catches this too, but the storage listener fires
 *      immediately without waiting for the query to resolve.
 *
 * The `storage` event fires ONLY in other tabs, never in the tab
 * that made the change — so there's no risk of self-triggering on
 * an intentional logout.
 */
export const useAuthTabSync = (): void => {
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const queryClient = useQueryClient();

  useEffect(() => {
    function onStorage(e: StorageEvent): void {
      if (e.key !== 'auth-storage') return;
      // The new value can be null (localStorage.removeItem) or the
      // serialised state after a set(). If either the value went
      // away or the token inside it is now null, treat it as a
      // sign-out and mirror it locally.
      let signedOut = false;
      if (e.newValue === null) {
        signedOut = true;
      } else {
        try {
          const parsed = JSON.parse(e.newValue) as {
            state?: { token?: string | null };
          };
          if (!parsed.state?.token) signedOut = true;
        } catch {
          // Malformed payload — treat as signed out to be safe.
          signedOut = true;
        }
      }
      if (signedOut) {
        clearAuth();
        queryClient.clear();
        // Don't clobber /login if we're already there — otherwise
        // an in-flight React render can loop on the replace call.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.replace('/login');
        }
      }
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [clearAuth, queryClient]);
};
